import './TargetingTable.scss';

import { isEqual as _isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CellProps, Column, ColumnInstance, Row } from 'react-table';
import { useFlexLayout, useSortBy, useTable } from 'react-table';

import type { TargetingPreset } from '../../apis/graphql';
import type { TargetingTermValue } from '../../models';
import bem from '../../utils/bem';
import { genericMemo } from '../../utils/genericMemo';
import type { ListHelpers } from '../../utils/listHelpers';
import { workWithLocalStorage } from '../../utils/storage/localStorage';
import { getTargetingKey } from '../AdForm/Targeting/util/getTargetingKey';
import SortArrow from '../SortArrow';
import { SortState } from '../SortArrow/constants';
import { CustomDimension, DIMENSION_INCLUDE_EXCLUDE_LIST, GeoDimension } from '../TargetingDropdowns/constants';
import { ToggleSection } from '../ToggleSection';
import ToggleSwitch from '../ToggleSwitch';
import { LOCAL_STORAGE_EXPANDABLE_ROWS_STATE, MIN_COLLAPSIBLE_ROW_GROUP_SIZE } from './constants';
import TargetingTableActionCell from './TargetingTableActionCell';
import { assignIdFromUnsortedData, changeSortState, sortTargetingTableRowGroups } from './utils';
import type { GroupedRowData } from './utils/helpers';
import { buildRowGroups, generateRow, generateToggleRow, getExpandCollapseText } from './utils/helpers';

export type TargetingTableRowGroup = {
  groupType?: string;
  entityName?: string;
  targetingTermValues: TargetingTermValue[];
};

type ListHelpersPartial = Pick<ListHelpers<TargetingTermValue>, 'removeAt' | 'replaceAt' | 'removeAll'>;

export type TargetingTableProps = Readonly<{
  data: TargetingTableRowGroup[];
  listHelpers?: ListHelpersPartial;
  editMode?: boolean;
  isAd?: boolean;
  isTableToggleable?: boolean;
  isTableDataFiltered?: boolean;
  filteredTargetingValues?: Set<string>;
  targetingPreset?: TargetingPreset;
  /**
   * Which entity the user is editing in the surrounding host. Forwarded
   * verbatim to the row-level `TargetingTableActionCell`. See that component
   * for details.
   *
   * Optional only because `targetingPreset` callers swap the action column
   * for a read-only preset toggle and never reach the action cell -- those
   * callers may omit this prop.
   */
  editingEntityType?: 'ad' | 'lineItem';
}>;

const [block, element] = bem('targeting-table');

type TargetingTermsTableContext = {
  groupType: string | undefined;
  rowGroupIndex: number;
  data: TargetingTableRowGroup[];
  prepareRow: (row: Row<TargetingTermValue>) => void;
  isTableDataFiltered: boolean;
  filteredTargetingValues?: Set<string>;
  expandedGroups: Record<string, boolean>;
  setExpandedGroups: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  completeGroupedData: Record<string, boolean>;
  nThRowKey: { value: number };
};

function buildFlatGroupRows(items: Row<TargetingTermValue>[], ctx: TargetingTermsTableContext): React.JSX.Element[] {
  return items.reduce((accum, termValueRow, rowIndexWithinGroup) => {
    const rowKey = ctx.nThRowKey.value++;
    termValueRow.index = assignIdFromUnsortedData(ctx.data, ctx.groupType!, termValueRow);
    ctx.prepareRow(termValueRow);

    if (ctx.isTableDataFiltered && !ctx.filteredTargetingValues?.has(getTargetingKey(termValueRow.original))) {
      return accum;
    }
    return [...accum, generateRow(termValueRow, rowKey, rowIndexWithinGroup, ctx.groupType, element)];
  }, [] as React.JSX.Element[]);
}

function buildCollapsibleGroupRows(
  groupKey: string,
  items: Row<TargetingTermValue>[],
  ctx: TargetingTermsTableContext
): React.JSX.Element[] {
  const adOrLineItemGroup = groupKey + ctx.rowGroupIndex;
  const handleToggleGroup = (): void => {
    ctx.setExpandedGroups((prev) => ({ ...prev, [adOrLineItemGroup]: !prev[adOrLineItemGroup] }));
  };

  const rowGroup: React.JSX.Element[] = [];
  items.forEach((termValueRow, rowIndexWithinGroup) => {
    termValueRow.index = assignIdFromUnsortedData(ctx.data, ctx.groupType!, termValueRow);
    ctx.prepareRow(termValueRow);
    const rowInGroup = generateRow(
      termValueRow,
      ctx.nThRowKey.value++,
      rowIndexWithinGroup,
      ctx.groupType,
      element,
      items.length - 1,
      ctx.expandedGroups[adOrLineItemGroup],
      handleToggleGroup
    );

    if (ctx.isTableDataFiltered && !ctx.filteredTargetingValues?.has(getTargetingKey(termValueRow.original))) {
      return;
    }
    rowGroup.push(rowInGroup);
  });

  if (!rowGroup.length) return [];

  ctx.completeGroupedData[adOrLineItemGroup] =
    ctx.expandedGroups[adOrLineItemGroup] ||
    (workWithLocalStorage.getData(LOCAL_STORAGE_EXPANDABLE_ROWS_STATE) as boolean);

  return [
    generateToggleRow(rowGroup, ctx.expandedGroups[adOrLineItemGroup], element, handleToggleGroup, adOrLineItemGroup),
  ];
}

function buildGroupRows(
  groupKey: string,
  groupedRowData: { [key: string]: GroupedRowData },
  ctx: TargetingTermsTableContext
): React.JSX.Element[] {
  const { items } = groupedRowData[groupKey];
  return items.length < MIN_COLLAPSIBLE_ROW_GROUP_SIZE
    ? buildFlatGroupRows(items, ctx)
    : buildCollapsibleGroupRows(groupKey, items, ctx);
}

function constructTargetingTermsTable(
  targetingRows: Row<TargetingTermValue>[],
  ctx: TargetingTermsTableContext
): React.JSX.Element[] {
  const groupedRowData = buildRowGroups(targetingRows);
  const targetingTermsTable: React.JSX.Element[] = [];

  const countryGroupKey = Object.keys(groupedRowData).find(
    (key) => groupedRowData[key].items[0]?.original.dimension.id === GeoDimension.Country
  );
  const publisherGroupKey = Object.keys(groupedRowData).find(
    (key) => groupedRowData[key].items[0]?.original.dimension.id === CustomDimension.Publisher
  );

  if (countryGroupKey) {
    targetingTermsTable.push(...buildGroupRows(countryGroupKey, groupedRowData, ctx));
  }
  if (publisherGroupKey) {
    targetingTermsTable.push(...buildGroupRows(publisherGroupKey, groupedRowData, ctx));
  }
  for (const groupKey in groupedRowData) {
    if (groupKey !== countryGroupKey && groupKey !== publisherGroupKey) {
      targetingTermsTable.push(...buildGroupRows(groupKey, groupedRowData, ctx));
    }
  }

  if (targetingTermsTable.length === 0) {
    const emptyTableMsg = ctx.isTableDataFiltered ? 'No matching targeting' : 'No targeting added yet';
    targetingTermsTable.push(
      <span key="no-targeting" className={element('no-targeting')}>
        {emptyTableMsg}
      </span>
    );
  }
  return targetingTermsTable;
}

function TargetingValueCell({ value: cellValue }: Readonly<{ value: TargetingTermValue['value'] }>): JSX.Element {
  if (!cellValue?.displayName) return <>No Value found</>;
  return <>{cellValue.displayName}</>;
}

type ActionColumnConfig = Readonly<{
  editMode: boolean;
  listHelpers: ListHelpersPartial;
  editingEntityType: 'ad' | 'lineItem';
  changeToggleByDimension: (value: boolean, row: TargetingTermValue, subGroupType: string) => void;
}>;

// Extra props injected by `cell.render('Cell', { ... })` in helpers.tsx, merged
// on top of react-table's `CellProps` when the cell is rendered.
type ActionCellRendererProps = CellProps<TargetingTermValue> & {
  isLineItemTargetingTermValue: boolean;
};

// Defined at module scope (rather than inline inside `TargetingTable`) so that
// it isn't recreated on every parent render. Parent-scope data is forwarded
// via the column definition's `actionConfig` field, which react-table makes
// available on `props.column`.
function ActionCellRenderer(props: ActionCellRendererProps): JSX.Element {
  const { actionConfig } = props.column as ColumnInstance<TargetingTermValue> & {
    actionConfig: ActionColumnConfig;
  };

  return (
    <TargetingTableActionCell
      {...props}
      editMode={actionConfig.editMode}
      listHelpers={actionConfig.listHelpers}
      editingEntityType={actionConfig.editingEntityType}
      changeToggleByDimension={actionConfig.changeToggleByDimension}
    />
  );
}

function PresetPopupToggleCell({ row }: CellProps<TargetingTermValue>): JSX.Element {
  return <ToggleSwitch isOn={row.original.include} disabled={true} />;
}

const PRESET_POPUP_TOGGLE_COLUMN: Column<TargetingTermValue> = {
  Header: '',
  accessor: 'include',
  disableSortBy: true,
  Cell: PresetPopupToggleCell,
};

function getAgeMinMax(values: string[]): string {
  const valFilter: number[] = values.filter((val) => !Number.isNaN(val)).map((val) => parseInt(val, 10));
  const lower: number = Math.min(...valFilter);
  const upper: number | undefined = values.length > 1 ? Math.max(...valFilter) : undefined;

  return `${lower}${upper ? ' - ' + upper : ''}`;
}

function TargetingTable({
  data,
  listHelpers,
  editMode = true,
  isAd,
  isTableToggleable = true,
  isTableDataFiltered = false,
  filteredTargetingValues,
  targetingPreset,
  editingEntityType,
}: TargetingTableProps): JSX.Element {
  const [expandedRows, setExpandedRows] = useState<boolean[]>(new Array(data.length).fill(true));
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [tableSort, setTableSort] = useState(SortState.UNSORTED);
  const sortedData = useMemo(() => sortTargetingTableRowGroups(data, tableSort), [data, tableSort]);
  const targetingTermValuesData = useMemo(() => sortedData.flatMap(({ targetingTermValues }) => targetingTermValues), [
    sortedData,
  ]);

  const handleExpandCollapseClick = useCallback((): void => {
    setExpandedGroups((prevExpandedGroups) => {
      const allExpanded = Object.values(prevExpandedGroups).every((isExpanded) => isExpanded);

      return Object.keys(prevExpandedGroups).reduce<Record<string, boolean>>((acc, group) => {
        acc[group] = !allExpanded;
        return acc;
      }, {});
    });
  }, [setExpandedGroups]);

  const changeToggleByDimension = useCallback(
    (value: boolean, row: TargetingTermValue, subGroupType: string): void => {
      const type: string = row.dimension.name;
      const listGroup = sortedData.find((group) => group.groupType === subGroupType);

      if (!DIMENSION_INCLUDE_EXCLUDE_LIST.includes(row.dimension.id)) {
        // update all include values of the same dimensions
        listGroup?.targetingTermValues.forEach((rowToUpdate, idx) => {
          const currType: string = rowToUpdate.dimension.name;
          if (currType === type) {
            listHelpers?.replaceAt(idx, { ...rowToUpdate, include: value });
          }
        });
      } else {
        // match on row key instead of row object. row instance may change with filtering
        const rowIndex =
          listGroup?.targetingTermValues?.findIndex(
            (targetingTerm) => getTargetingKey(row) === getTargetingKey(targetingTerm)
          ) ?? 0;

        listHelpers?.replaceAt(rowIndex, { ...row, include: value });
      }
    },
    [listHelpers, sortedData]
  );

  const columns: Column<TargetingTermValue>[] = useMemo(
    (): Column<TargetingTermValue>[] => {
      // Default to 'ad' for the (rare) host that supplies `listHelpers`
      // without telling the panel which entity it's editing -- preserves
      // prior behavior (delete on ad rows, line items disabled).
      const actionConfig: ActionColumnConfig = {
        editMode,
        listHelpers: listHelpers as ListHelpersPartial,
        editingEntityType: editingEntityType ?? 'ad',
        changeToggleByDimension,
      };

      const actionColumn = {
        Header: (): React.JSX.Element => {
          return (
            <button className={element('expand-collapse-text')} onClick={handleExpandCollapseClick}>
              {getExpandCollapseText(expandedGroups)}
            </button>
          );
        },
        accessor: 'include',
        Cell: ActionCellRenderer,
        disableSortBy: true,
        maxWidth: 100,
        actionConfig,
      } as Column<TargetingTermValue>;

      let extraColumns: Column<TargetingTermValue>[] = [];
      if (targetingPreset !== undefined) {
        extraColumns = [PRESET_POPUP_TOGGLE_COLUMN];
      } else if (listHelpers) {
        extraColumns = [actionColumn];
      }

      return [
        {
          Header: 'Category',
          accessor: (a): string => a?.category?.displayName || 'No Category found',
          maxWidth: 100,
        },
        {
          Header: 'Type',
          accessor: (a): string => a?.dimension?.displayName || 'No Dimension found',
          disableSortBy: true,
          maxWidth: 80,
        },
        {
          Header: 'Value',
          accessor: 'value',
          Cell: TargetingValueCell,
          disableSortBy: true,
          maxWidth: 140,
        },
        ...extraColumns,
      ];
    },
    // eslint-disable-next-line
    [editMode, listHelpers, expandedGroups, changeToggleByDimension, editingEntityType, targetingPreset]
  );

  const tableInstance = useTable<TargetingTermValue>(
    {
      columns,
      data: targetingTermValuesData,
      autoResetHiddenColumns: false,
      manualSortBy: true,
    },
    useSortBy,
    useFlexLayout
  );

  useEffect(() => {
    setTableSort(SortState.UNSORTED);
  }, [data]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  const handleClearSelections = useCallback(
    (e: React.MouseEvent): void => {
      e.preventDefault();
      listHelpers?.removeAll();
    },
    [listHelpers]
  );

  let completeGroupedData: Record<string, boolean> = {};
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if ((!hasInitializedRef.current && data.length > 0) || editMode) {
      if (!_isEqual(completeGroupedData, expandedGroups)) {
        setExpandedGroups(completeGroupedData);
      }
      hasInitializedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const nThRowKey = { value: 0 };

  return (
    <table {...getTableProps()} cellSpacing={0} cellPadding={0} className={block()}>
      <thead>
        <tr {...headerGroups[0].getHeaderGroupProps()}>
          {headerGroups[0].headers.map((column, headerIndex) => (
            <th
              tabIndex={-1}
              {...column.getHeaderProps(column.getSortByToggleProps())}
              key={`table-head-${headerIndex}`}
            >
              <div className={element('header-cell')}>
                {column.render('Header')}
                {column.canSort && (
                  <SortArrow
                    isSorted={tableSort !== SortState.UNSORTED}
                    isDescending={tableSort === SortState.SORTED_DESC}
                    onClick={(): void => setTableSort(changeSortState(tableSort))}
                  />
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody {...getTableBodyProps()} key="targeting-table-body">
        {sortedData.map(({ targetingTermValues, groupType, entityName }, rowGroupIndex) => {
          const handleToggle = (): void => {
            setExpandedRows((prevExpandedRows) => {
              const updatedExpandedRows = [...prevExpandedRows];
              updatedExpandedRows[rowGroupIndex] = !prevExpandedRows[rowGroupIndex];
              return updatedExpandedRows;
            });
          };
          const ageValArr: string[] = targetingTermValues
            .filter((entry) => entry.dimension.id === 'age')
            .map((entry) => entry.value.id);
          const sliceRowIndex = rowGroupIndex ? sortedData[rowGroupIndex - 1].targetingTermValues.length : 0;

          const isShowAction =
            targetingTermValues.length > 0 &&
            editMode &&
            ((!isAd && groupType === 'Line Item Targeting') || (isAd && groupType === 'Ad Targeting'));

          const TableToggleWrapper = ({ children }: { children: JSX.Element | JSX.Element[] }): JSX.Element => (
            <div className={element('toggle-cell')}>
              <ToggleSection
                title={
                  <div className={element('section-title')}>
                    <div>
                      {groupType}
                      <span>
                        {entityName ? ` - ${entityName}` : ' '} ({targetingTermValues.length})
                      </span>
                    </div>
                  </div>
                }
                isExpanded={expandedRows[rowGroupIndex]}
                handleToggle={handleToggle}
                key={`toggle-section-${rowGroupIndex}`}
                toggleClassName={`${element('toggle')}`}
                action={
                  isShowAction && (
                    <button className={element('clear')} title="Clear Selections" onClick={handleClearSelections}>
                      Clear Selections
                    </button>
                  )
                }
              >
                {children}
              </ToggleSection>
            </div>
          );

          const ctx: TargetingTermsTableContext = {
            groupType,
            rowGroupIndex,
            data,
            prepareRow,
            isTableDataFiltered,
            filteredTargetingValues,
            expandedGroups,
            setExpandedGroups,
            completeGroupedData,
            nThRowKey,
          };
          const targetingTermsTable = constructTargetingTermsTable(
            rows.slice(sliceRowIndex, sliceRowIndex + targetingTermValues.length),
            ctx
          );
          const contentElem = isTableToggleable ? (
            <TableToggleWrapper key={`${rowGroupIndex}-toggle-wrapper`}>{targetingTermsTable}</TableToggleWrapper>
          ) : (
            targetingTermsTable
          );

          return (
            <tr className={element('toggle-section-row')} key={`${rowGroupIndex}-toggle-section-row`}>
              <td className={element('toggle-cell')}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {ageValArr.length ? (
                    <div>{`NOTE: You selected Age values '${ageValArr.join(', ')}' your value will be '${getAgeMinMax(
                      ageValArr
                    )}'`}</div>
                  ) : null}
                  {contentElem}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default genericMemo(TargetingTable);
